import { useQuery } from 'convex/react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { permissionIncludes } from '@bill-alps/backend/features/companies/access';
import {
  isModuleEnabled,
  type ModuleKey,
} from '@bill-alps/backend/features/companies/modules';
import { MODULE_AVAILABLE } from '@/lib/runtime/feature-flags';

export function useCurrentUser() {
  const user = useQuery(api.features.users.profile.getCurrentUser);
  const permissionFor = (module: ModuleKey) =>
    user?.companyAccess?.permissions[module] ?? 'none';
  // A mandate (client company) is active — the trustee works FOR a client, so
  // account/billing surfaces hide and the company page wears the mandate name.
  // Joined companies never count: mandates are owned by the trustee.
  const isMandateSelected =
    !!user?.fiduciaire &&
    user?.companyAccess?.role === 'owner' &&
    !!user?.company &&
    user.company._id !== user.fiduciaire.ownCompanyId;

  return {
    user: user ?? null,
    isLoading: user === undefined,
    hasCompany: user?.hasCompany ?? false,
    status: user?.status ?? null,
    isActive: user?.status === 'active',
    isSupport: user?.role === 'ROLE_SUPPORT',
    isAdmin: user?.email?.endsWith('@w-ap.ch') ?? false,
    fiduciaire: user?.fiduciaire ?? null,
    isFiduciaire: !!user?.fiduciaire,
    isMandateSelected,
    companyAccess: user?.companyAccess,
    availableCompanies: user?.availableCompanies ?? [],
    suspendedCompanies: user?.suspendedCompanies ?? [],
    isCompanyOwner: user?.companyAccess?.role === 'owner',
    isAccountAdmin:
      user?.companyAccess?.role === 'owner' ||
      user?.companyAccess?.role === 'admin',
    canReadModule: (module: ModuleKey) =>
      permissionIncludes(permissionFor(module), 'read'),
    // Ask this before firing a query whose Convex handler calls
    // `assertCanReadCompanyModule`: the same triple `ModuleGate` applies, so a
    // page that is NOT behind the gate (e.g. /company) does not blow up on a
    // module the company turned off. Includes the deploy kill switch because
    // the server checks `isModuleAvailable` too.
    canUseModule: (module: ModuleKey) =>
      MODULE_AVAILABLE[module] &&
      isModuleEnabled(user?.company, module) &&
      permissionIncludes(permissionFor(module), 'read'),
    canWriteModule: (module: ModuleKey) =>
      permissionIncludes(permissionFor(module), 'write'),
  };
}
